import React from 'react';
import {
  Typography,
  Box,
  styled,
  Link,
  LinkProps,
  useTheme,
} from '@material-ui/core';

import { useTranslation } from 'react-i18next';

import CallIcon from '@/assets/Call.svg';
import ChatIcon from '@/assets/Chat.svg';
import CrisisTextLine from '@/assets/CrisisTextLine.svg';

const ContentContainer = styled(Box)({
  marginTop: 32,
  '& > *': {
    marginBottom: 24,
  },
});

const CrisisResourceContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
  marginTop: 16,
  padding: 16,
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}));

const List = styled(Box)(({ theme }) => ({
  margin: 0,
  marginTop: 10,
  paddingLeft: theme.spacing(1.5),
  paddingRight: 0,
  listStyle: 'disc',
  listStylePosition: 'outside',
  '& li': {
    marginBottom: theme.spacing(1),
  },
  '& li::marker': {
    color: theme.palette.secondary.main,
  },
}));

const ResourceLink = (props: LinkProps) => {
  const theme = useTheme();
  return (
    <Box sx={{ color: theme.palette.text.primary, display: 'inline-block' }}>
      <Link
        target="_blank"
        rel="noopener noreferrer"
        underline="always"
        color="inherit"
        style={{ wordWrap: 'break-word' }}
        {...props}
      />
    </Box>
  );
};

const SleepioResources = () => {
  const { t } = useTranslation();
  return (
    <>
      <Box style={{ marginTop: 30 }}>
        <Typography variant="h2">
          {t('coverage.sleepio.rejection.emergency')}
        </Typography>
        <Typography style={{ marginTop: 8 }}>
          {t('coverage.sleepio.rejection.resource')}
        </Typography>
      </Box>
      <List component="ul">
        <li>
          <Typography>
            {t('coverage.sleepio.rejection.medical_or_mental')}
            {t('coverage.sleepio.rejection.dial_911')}
            <span style={{ fontWeight: 700 }}>911</span>
            {t('coverage.sleepio.rejection.emergency_room')}
          </Typography>
        </li>
        <li>
          <Typography>
            {t('coverage.sleepio.rejection.health_questions')}
          </Typography>
        </li>
        <li>
          <Typography>
            {t('coverage.sleepio.rejection.crisis_support')}
          </Typography>
        </li>
      </List>
      <CrisisResourceContainer>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.suicide_lifeline')}
        </Typography>
        <Typography style={{ display: 'flex', gap: 8 }}>
          <div>
            <img src={CallIcon} alt="Call icon" />
          </div>
          <div>
            <ResourceLink href="tel:988">
              {t('coverage.sleepio.rejection.call')}
              {' '}
              988
            </ResourceLink>
          </div>
        </Typography>
        <Typography style={{ display: 'flex', gap: 8 }}>
          <div>
            <img src={ChatIcon} alt="Chat icon" />
          </div>
          <div>
            {t('coverage.sleepio.rejection.chat_online')}
            {' '}
            <ResourceLink href="www.988lifeline.org/chat/">
              www.988lifeline.org/chat/
            </ResourceLink>
          </div>
        </Typography>
      </CrisisResourceContainer>
      <CrisisResourceContainer>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.crisis_text_label')}
        </Typography>
        <Typography style={{ display: 'flex', gap: 8 }}>
          <div>
            <img src={CrisisTextLine} alt="Crisis Text Line icon" />
          </div>
          <div>
            {t('coverage.sleepio.rejection.crisis_text')}
            {' '}
            <ResourceLink href="tel:741741">741741</ResourceLink>
          </div>
        </Typography>
      </CrisisResourceContainer>
      <ContentContainer>
        <Typography variant="h2">
          {t('coverage.sleepio.rejection.additional_resources')}
        </Typography>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.services_administration')}
        </Typography>
        <Typography>
          <ResourceLink href="https://www.samhsa.gov/find-help/national-helpline">
            https://www.samhsa.gov/find-help/national-helpline
          </ResourceLink>
        </Typography>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.psychological_association')}
        </Typography>
        <Typography>
          <ResourceLink href="https://locator.apa.org/">
            https://locator.apa.org/
          </ResourceLink>
        </Typography>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.sleep_academy')}
        </Typography>
        <Typography>
          <ResourceLink href="https://sleepeducation.org/">
            https://sleepeducation.org/
          </ResourceLink>
        </Typography>
        <Typography>{t('coverage.sleepio.rejection.disclaimer')}</Typography>
      </ContentContainer>
    </>
  );
};

export default SleepioResources;
